// Procedural streaming soundtrack for Lumen Abyss.
// Seeded Lydian theme generator + lookahead WebAudio scheduler.
// No downloaded samples; deterministic per run seed (no Math.random
// anywhere in the generator or noise buffers).

// Lydian scale intervals (semitones from root): 1 2 3 #4 5 6 7.
// The raised 4th gives the bright "neon" character.
const SCALE_LYDIAN = [0, 2, 4, 6, 7, 9, 11];

// Diatonic triads of the key expressed as scale-degree stacks in thirds.
const TRIAD_AT = (degree: number): number[] => [degree, degree + 2, degree + 4];

// 4-bar progressions as scale degrees (0 = I). Lydian's signature is the
// I–II major lift, so every progression leans on degree 1 (II) or 4 (V)
// and resolves to the tonic so the loop reads as a phrase.
const PROGRESSIONS: readonly number[][] = [
  [0, 1, 4, 0], // I – II  – V  – I   (signature lydian lift)
  [0, 4, 1, 0], // I – V   – II – I
  [0, 6, 1, 4], // I – vii – II – V
  [0, 2, 6, 1], // I – iii – vii – II
  [0, 5, 1, 4], // I – vi  – II – V
];

// Rhythm masks: 16 sixteenth-note slots per bar. 1 = onset, 0 = rest.
// Hand-picked so every pattern lands a note on beat 1 and at least one
// syncopation somewhere. The generator picks one per bar.
const RHYTHMS: readonly number[][] = [
  [1, 0, 1, 0, 1, 0, 0, 1, 1, 0, 1, 0, 1, 0, 1, 0],
  [1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 1, 0, 1],
  [1, 1, 0, 0, 1, 0, 1, 0, 0, 1, 0, 1, 1, 0, 1, 0],
  [1, 0, 1, 0, 0, 1, 1, 0, 1, 0, 0, 1, 0, 1, 0, 0],
  [1, 0, 0, 1, 1, 0, 1, 0, 1, 0, 0, 1, 1, 0, 1, 0],
];

// Mulberry32 — same PRNG as src/sim/rng.ts, kept local so the music
// engine has no sim dependency and can be driven from any seed.
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(rnd: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rnd() * arr.length)];
}

// Translate (rootMidi, degree) + scale into a MIDI note number.
// `degree` may be negative or >= scale length — wraps across octaves.
function scaleNote(rootMidi: number, degree: number): number {
  const len = SCALE_LYDIAN.length;
  const octave = Math.floor(degree / len);
  const idx = ((degree % len) + len) % len;
  return rootMidi + octave * 12 + SCALE_LYDIAN[idx];
}

function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export interface MusicNote {
  startSec: number;
  durSec: number;
  midi: number;
  velocity: number;
}

export interface Theme {
  pad: MusicNote[];
  lead: MusicNote[];
  bpm: number;
  rootMidi: number;
  secPerBeat: number;
  duration: number; // seconds for the full 4-bar loop
}

// Generate a deterministic 4-bar theme from a seed: root around D4,
// BPM 112–128, a diatonic Lydian progression, whole-bar pad triads and
// a sixteenth-note lead melody biased toward chord tones.
export function generateTheme(seed: number): Theme {
  const rnd = mulberry32(seed);
  const bars = 4;
  const rootMidi = 60 + Math.floor(rnd() * 5); // C4–E4, centered on D4
  const bpm = 96 + Math.floor(rnd() * 17); // 96–112: calmer, less chaotic
  const secPerBeat = 60 / bpm;
  const secPerBar = secPerBeat * 4;
  const sixteenth = secPerBeat / 4;
  const progression = pick(rnd, PROGRESSIONS);

  // Pad track: one whole-bar triad per bar, an octave below the lead.
  const pad: MusicNote[] = [];
  for (let bar = 0; bar < bars; bar++) {
    const degree = progression[bar % progression.length];
    for (const d of TRIAD_AT(degree)) {
      pad.push({
        startSec: bar * secPerBar,
        durSec: secPerBar,
        midi: scaleNote(rootMidi - 12, d),
        velocity: 56,
      });
    }
  }

  // Lead track: sixteenth-note grid, one rhythm mask per bar. 70 %
  // chord tones, 30 % stepwise passing tones from the previous note.
  const lead: MusicNote[] = [];
  let lastDegree = 0;
  for (let bar = 0; bar < bars; bar++) {
    const rhythm = pick(rnd, RHYTHMS);
    const chordDegree = progression[bar % progression.length];
    const chordTones = TRIAD_AT(chordDegree);
    for (let step = 0; step < 16; step++) {
      if (!rhythm[step]) continue;
      let degree: number;
      if (rnd() < 0.8) {
        degree = chordTones[Math.floor(rnd() * chordTones.length)];
      } else {
        degree = lastDegree + (rnd() < 0.5 ? -1 : 1);
      }
      // Keep the melody inside a one-octave window above the root so it
      // doesn't wander into screeching territory.
      while (degree < 0) degree += 7;
      while (degree > 12) degree -= 7;
      lastDegree = degree;
      lead.push({
        startSec: bar * secPerBar + step * sixteenth,
        durSec: sixteenth * 0.9,
        midi: scaleNote(rootMidi, degree),
        velocity: 62 + Math.floor(rnd() * 18),
      });
    }
  }

  return { pad, lead, bpm, rootMidi, secPerBeat, duration: bars * secPerBar };
}

// Nodes of one scheduled voice, tracked so stop() can silence everything
// in flight and the pruner can release nodes whose time has passed.
interface TrackedVoice {
  o: OscillatorNode | AudioBufferSourceNode;
  g: GainNode;
  endAt: number;
}

const PLAYS_PER_THEME = 4;
const LOOKAHEAD_SEC = 0.3;

export class MusicEngine {
  private ctx: AudioContext | null = null;
  private output: GainNode | null = null;
  private volume = 0.7;
  private seed = 1;
  private themeIndex = 0;
  private playing = false;
  private current: Theme | null = null;
  private upcoming: Theme | null = null;
  private playsDone = 0;
  private nextStartTime = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly active = new Set<TrackedVoice>();
  private noiseBuffer: AudioBuffer | null = null;

  // Wire the engine to a live context. `destination` is the music bus
  // owned by AudioEngine. Safe to call once; later calls are no-ops.
  init(ctx: AudioContext, destination: AudioNode): void {
    if (this.ctx) return;
    this.ctx = ctx;
    this.output = ctx.createGain();
    this.output.gain.value = this.volume;
    this.output.connect(destination);
  }

  // Set the run seed. Deterministic: theme N of a run always uses the
  // same derived seed. Clears any pre-generated upcoming theme so a
  // stale one from a previous run can't leak in.
  setSeed(seed: number): void {
    this.seed = seed >>> 0;
    this.themeIndex = 0;
    this.upcoming = null;
  }

  // Start the streaming loop. No-op until init() ran or while playing.
  start(): void {
    const ctx = this.ctx;
    if (!ctx || !this.output || this.playing) return;
    this.playing = true;
    this.themeIndex = 0;
    this.playsDone = 0;
    this.upcoming = null;
    this.current = generateTheme(this.themeSeed(0));
    this.nextStartTime = ctx.currentTime + 0.1;
    this.tick();
  }

  // Silence everything in flight and stop the scheduler.
  stop(): void {
    this.playing = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.current = null;
    this.upcoming = null;
    this.playsDone = 0;
    const t = this.ctx ? this.ctx.currentTime : 0;
    for (const v of this.active) {
      try {
        v.g.gain.cancelScheduledValues(t);
        v.g.gain.linearRampToValueAtTime(0, t + 0.15);
        v.o.stop(t + 0.2);
      } catch {
        // already stopped / disconnected
      }
    }
    this.active.clear();
  }

  // 0..1 linear gain on the music bus output.
  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    if (this.ctx && this.output) {
      this.output.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.05);
    }
  }

  isPlaying(): boolean {
    return this.playing;
  }

  // Per-theme derived seed: run seed + golden-ratio step per index, so
  // successive themes differ but stay reproducible for the same run seed.
  private themeSeed(index: number): number {
    return (this.seed + Math.imul(index, 0x9E3779B9)) >>> 0;
  }

  // Streaming scheduler: schedule one pass of the current theme, then
  // setTimeout to wake ~300 ms before the next pass is due. Each theme
  // plays PLAYS_PER_THEME times; the follow-up theme is generated during
  // the first pass so the handoff is seamless.
  private tick(): void {
    const ctx = this.ctx;
    if (!this.playing || !ctx || !this.current) return;
    this.prune();
    // If the tab was suspended, fast-forward instead of stacking passes.
    if (this.nextStartTime < ctx.currentTime - 0.5) {
      this.nextStartTime = ctx.currentTime + 0.1;
    }
    const theme = this.current;
    this.schedulePass(theme, this.nextStartTime);
    this.nextStartTime += theme.duration;
    this.playsDone++;

    // Pre-generate the next theme as soon as the first pass is queued.
    // Generation is pure seeded math (sub-ms), so a synchronous build
    // here keeps us well ahead of the ~8 s pass boundary.
    if (this.playsDone === 1 && !this.upcoming) {
      this.upcoming = generateTheme(this.themeSeed(this.themeIndex + 1));
    }

    if (this.playsDone >= PLAYS_PER_THEME) {
      this.themeIndex++;
      this.current = this.upcoming ?? generateTheme(this.themeSeed(this.themeIndex));
      this.upcoming = null;
      this.playsDone = 0;
    }

    const msUntil = Math.max(20, (this.nextStartTime - ctx.currentTime - LOOKAHEAD_SEC) * 1000);
    this.timer = setTimeout(() => this.tick(), msUntil);
  }

  // Schedule one full play of a theme starting at audio time `startAt`.
  private schedulePass(theme: Theme, startAt: number): void {
    for (const n of theme.pad) {
      const t0 = startAt + n.startSec;
      this.schedulePadNote(t0, t0 + n.durSec, midiToFreq(n.midi), n.velocity);
    }
    for (const n of theme.lead) {
      const t0 = startAt + n.startSec;
      this.scheduleLeadNote(t0, t0 + n.durSec, midiToFreq(n.midi), n.velocity);
    }
    const beats = Math.max(1, Math.round(theme.duration / theme.secPerBeat));
    for (let b = 0; b < beats; b++) {
      const t = startAt + b * theme.secPerBeat;
      this.scheduleKick(t);
      // Backbeat — beats 2 and 4 are the odd-indexed beats.
      if (b % 2 === 1) this.scheduleClap(t);
    }
  }

  // Lead voice: two detuned sawtooths through a resonant lowpass with a
  // fast envelope, plus a square sub-osc an octave down for body.
  private scheduleLeadNote(t0: number, t1: number, freq: number, velocity: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.output) return;
    const peak = Math.min(0.16, (velocity / 127) * 0.18);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(Math.min(5200, freq * 6 + 600), t0);
    filter.frequency.exponentialRampToValueAtTime(Math.max(400, freq * 2.2), t1);
    filter.Q.value = 6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + 0.008);
    g.gain.linearRampToValueAtTime(peak * 0.55, t0 + 0.06);
    g.gain.setTargetAtTime(0.0001, Math.max(t0 + 0.07, t1 - 0.12), 0.05);
    filter.connect(g);
    g.connect(this.output);

    const endAt = t1 + 0.15;
    for (const detune of [-6, 6]) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      osc.detune.value = detune;
      osc.connect(filter);
      osc.start(t0);
      osc.stop(endAt);
      this.active.add({ o: osc, g, endAt });
    }
    const sub = ctx.createOscillator();
    sub.type = 'square';
    sub.frequency.value = freq * 0.5;
    const subGain = ctx.createGain();
    subGain.gain.setValueAtTime(0.25, t0);
    sub.connect(subGain);
    subGain.connect(filter);
    sub.start(t0);
    sub.stop(endAt);
    this.active.add({ o: sub, g: subGain, endAt });
  }

  // Pad voice: softer detuned saws through a gentle lowpass with a slow
  // swell and release, plus a triangle an octave down for warmth.
  private schedulePadNote(t0: number, t1: number, freq: number, velocity: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.output) return;
    const dur = t1 - t0;
    const peak = Math.min(0.07, (velocity / 127) * 0.09);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = Math.min(2400, freq * 4);
    filter.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + Math.min(1.0, dur * 0.25));
    g.gain.setTargetAtTime(0.0001, t1 - Math.min(1.2, dur * 0.4), 0.3);
    filter.connect(g);
    g.connect(this.output);

    const endAt = t1 + 1.0;
    for (const detune of [-7, 7]) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = freq;
      osc.detune.value = detune;
      osc.connect(filter);
      osc.start(t0);
      osc.stop(endAt);
      this.active.add({ o: osc, g, endAt });
    }
    const tri = ctx.createOscillator();
    tri.type = 'triangle';
    tri.frequency.value = freq * 0.5;
    const triGain = ctx.createGain();
    triGain.gain.setValueAtTime(0.5, t0);
    tri.connect(triGain);
    triGain.connect(filter);
    tri.start(t0);
    tri.stop(endAt);
    this.active.add({ o: tri, g: triGain, endAt });
  }

  // Kick drum: sine sliding 150 Hz → 40 Hz over 80 ms with a snappy
  // amplitude envelope. Four-on-the-floor.
  private scheduleKick(t0: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.output) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, t0);
    osc.frequency.exponentialRampToValueAtTime(40, t0 + 0.08);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.linearRampToValueAtTime(0.5, t0 + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
    osc.connect(g);
    g.connect(this.output);
    osc.start(t0);
    osc.stop(t0 + 0.25);
    this.active.add({ o: osc, g, endAt: t0 + 0.3 });
  }

  // Clap voice: three short band-passed noise bursts ~8 ms apart.
  private scheduleClap(t0: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.output) return;
    const buf = this.getNoiseBuffer();
    for (const dly of [0, 0.008, 0.016]) {
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 1500;
      bp.Q.value = 1.2;
      const g = ctx.createGain();
      const t = t0 + dly;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.28, t + 0.002);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
      src.connect(bp);
      bp.connect(g);
      g.connect(this.output);
      src.start(t);
      src.stop(t + 0.12);
      this.active.add({ o: src, g, endAt: t + 0.15 });
    }
  }

  // Half-second white-noise buffer for the clap voice, filled from a
  // seeded mulberry32 (not Math.random) to keep the engine deterministic.
  private getNoiseBuffer(): AudioBuffer {
    const ctx = this.ctx;
    if (!ctx) throw new Error('MusicEngine not initialized');
    if (this.noiseBuffer) return this.noiseBuffer;
    const frames = Math.max(1, Math.floor(ctx.sampleRate * 0.5));
    const buf = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buf.getChannelData(0);
    const rnd = mulberry32(0xC1A9);
    for (let i = 0; i < data.length; i++) {
      data[i] = rnd() * 2 - 1;
    }
    this.noiseBuffer = buf;
    return buf;
  }

  // Release nodes whose stop time has passed so the browser can GC them
  // and we don't approach the AudioContext node ceiling on long runs.
  private prune(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    for (const v of this.active) {
      if (now > v.endAt) {
        try {
          v.o.disconnect();
          v.g.disconnect();
        } catch {
          // already disconnected
        }
        this.active.delete(v);
      }
    }
  }
}
